import 'animate.css';

import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { LatLng, RoutalLoader, RoutalPalette } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Back from '../../assets/img/onboarding/icons/back.svg';
import CompanyNameIllustration from '../../assets/img/onboarding/illustrations/company-name.png';
import ConstraintsIllustration from '../../assets/img/onboarding/illustrations/constraints.png';
import AccessDriversIllustration from '../../assets/img/onboarding/illustrations/drivers.png';
import FleetIllustration from '../../assets/img/onboarding/illustrations/fleet.png';
import { FloatingLayout, OnboardingStepProps } from '../../components/public-layout/FloatingLayout';
import { ProjectPlans } from '../../domain/project/ProjectPlans';
import { useRetrieveOrganization } from '../../hooks/organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useRetrieveSessionCountry } from '../../hooks/session/useRetrieveSessionCountry';
import { useRetrieveUser } from '../../hooks/user/useRetrieveUser';
import { Map } from '../../react-components/Map';
import Text from '../../react-components/Text';
import { OnboardingContainer } from './Onboarding.styled';
import { OnboardingTopBar } from './OnboardingTopBar';
import { useInitializeOnboarding } from './hooks/useInitializeOnboarding';
import { AccessDrivers } from './steps/AccessDrivers';
import { CompanyLocation } from './steps/CompanyLocation';
import { CompanyName } from './steps/CompanyName';
import { Constraints } from './steps/Constraints';
import { OnboardingImageRightComponent } from './steps/OnboardingImageRightComponent';
import { OnboardingStart } from './steps/OnboardingStart';
import { NumVehicles } from './steps/num_of_vehicles/NumVehicles';

const availableSteps = [
  `start`,
  `companyName`,
  `numVehicles`,
  `constraints`,
  `companyLocation`,
  `driversAccess`,
] as const;

type AvailableSteps = (typeof availableSteps)[number];

export const OnboardingComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: organization } = useRetrieveOrganization();
  const { data: user } = useRetrieveUser();

  const {
    data: onboardingData,
    isLoading: isCreatingOnboardingData,
    run: createOnboardingData,
  } = useInitializeOnboarding();
  const { countryCode } = useRetrieveSessionCountry();
  const [step, setStep] = useState<AvailableSteps | undefined>(undefined);
  const [organizationLocation, setOrganizationLocation] = useState<Location | undefined>(organization?.location);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const urlStepParam = searchParams.get(`step`);

    // check if urlStepParam is a number
    const urlStep = urlStepParam ?? ``;
    if (!availableSteps.includes(urlStep as AvailableSteps)) {
      searchParams.set(`step`, availableSteps[0]);
      setSearchParams(searchParams);
      onChangeStep(0);
    } else if (urlStep !== step) {
      searchParams.set(`step`, urlStep);
      setSearchParams(searchParams);
      setStep(urlStep as AvailableSteps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (organization) {
      if (!onboardingData && !isCreatingOnboardingData) {
        const plan_name = ProjectPlans.labelTemplateToValue({
          template: activeProject?.plans?.label ?? `{{day_name}} - Plan`,
          date: new Date(),
          language: user?.language.value,
        });
        createOnboardingData(plan_name);
      }
      if (organization.location?.isValid()) {
        setOrganizationLocation(organization.location);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatingOnboardingData, onboardingData, organization]);

  const onChangeStep = (newStepPosition: number) => {
    searchParams.set(`step`, availableSteps[newStepPosition]);
    setSearchParams(searchParams);
  };

  const getStepPosition = () => (step ? availableSteps.indexOf(step) : 0);

  const getStepContent = () => {
    let leftElement: React.ReactElement | null = null;
    let rightElement: React.ReactElement | null = null;
    let backgroundElement: React.ReactElement | null = null;
    const leftElementStyle: { widthPercent: number; style: React.CSSProperties } = { widthPercent: 47, style: {} };
    const backButton: React.ReactElement | null = (
      <div
        style={{
          textAlign: `end`,
          padding: `10px 20px`,
          position: `absolute`,
          left: 0,
          bottom: 0,
          zIndex: 1,
        }}
      >
        <Grid
          container
          direction="row"
          onClick={() => {
            setSuccess(false);
            onChangeStep(getStepPosition() - 1);
          }}
        >
          <Grid item marginRight={`4px`}>
            <img src={Back} alt="" draggable="false" style={{ width: `18px`, cursor: `pointer` }} />
          </Grid>

          <Grid item display={`flex`} style={{ marginTop: `1px` }}>
            <Text intlId="company.onboarding.back" variant="button" style={{ cursor: `pointer` }} />
          </Grid>
        </Grid>
      </div>
    );
    const nextButton: React.ReactElement | null = null;
    let log_out_color = RoutalPalette.neutral00;

    const onboardingStepProps: OnboardingStepProps = {
      step: getStepPosition(),
      totalSteps: availableSteps.length,
      backButton,
      nextButton,
    };

    switch (step) {
      case availableSteps[0]:
        return (
          <OnboardingStart
            onNextStep={() => {
              onChangeStep(1);
            }}
          />
        );
      case availableSteps[1]:
        leftElement = (
          <CompanyName
            onFinish={() => {
              setSuccess(true);
            }}
          />
        );
        rightElement = (
          <OnboardingImageRightComponent
            titleIntlId="company.onboarding.name.right_claim.title"
            descriptionIntlId="company.onboarding.name.right_claim.description"
            illustration={CompanyNameIllustration}
          />
        );
        break;
      case availableSteps[2]:
        leftElement = (
          <NumVehicles
            initialValue={organization?.num_of_vehicles}
            onNextStep={() => {
              setSuccess(true);
            }}
          />
        );
        rightElement = (
          <OnboardingImageRightComponent
            titleIntlId="company.onboarding.num_of_vehicles.right_claim.title"
            descriptionIntlId="company.onboarding.num_of_vehicles.right_claim.description"
            illustration={FleetIllustration}
          />
        );
        break;
      case availableSteps[3]:
        if (!activeProject) {
          return null;
        }
        leftElement = (
          <Grid container justifyContent="center" alignItems="center">
            <Constraints
              project={activeProject}
              onFinish={() => {
                onChangeStep(4);
              }}
            />
          </Grid>
        );
        rightElement = (
          <OnboardingImageRightComponent
            titleIntlId="company.onboarding.constraints.right_claim.title"
            descriptionIntlId="company.onboarding.constraints.right_claim.description"
            illustration={ConstraintsIllustration}
          />
        );
        break;
      case availableSteps[4]:
        if (!organization || !activeProject) {
          return null;
        }
        log_out_color = RoutalPalette.primary40;
        leftElement = (
          <Grid container justifyContent="center" alignItems="center">
            <CompanyLocation
              activeProject={activeProject}
              organizationLocation={organizationLocation}
              onChange={setOrganizationLocation}
              onFinish={() => {
                onChangeStep(5);
              }}
            />
          </Grid>
        );
        rightElement = <div></div>;
        backgroundElement = (
          <div
            style={{
              position: `absolute`,
              right: `0`,
              background: RoutalPalette.primary40,
              width: `55%`,
              height: `calc(100% - 20px)`,
              margin: `10px`,
              borderRadius: `10px`,
            }}
            className="animate__animated animate__fadeIn"
          >
            <Map
              key="map"
              height={`100%`}
              borderRadius={`10px`}
              center={
                organizationLocation?.isValid()
                  ? (organizationLocation!.toCoordinate().getValue() as LatLng)
                  : undefined
              }
              zoomLevel={10}
              language={(organization.country_code ?? countryCode).toLowerCase()}
              markers={
                organizationLocation?.isValid()
                  ? [
                      {
                        id: organization.id,
                        lat: organizationLocation!.lat!,
                        lng: organizationLocation!.lng!,
                        // selected: true,
                        color: RoutalPalette.primary40,
                        kind: `routal_star`,
                      },
                    ]
                  : []
              }
            />
          </div>
        );
        break;
      case availableSteps[5]:
        if (!onboardingData || isCreatingOnboardingData) {
          leftElement = (
            <Grid container alignItems={`center`} flexDirection={`column`}>
              <RoutalLoader />
            </Grid>
          );
        } else {
          leftElement = <AccessDrivers onboardingData={onboardingData!} projectId={activeProject!.id} />;
          rightElement = (
            <OnboardingImageRightComponent
              titleIntlId="company.onboarding.drivers.right_claim.title"
              descriptionIntlId="company.onboarding.drivers.right_claim.description"
              illustration={AccessDriversIllustration}
            />
          );
        }
        break;
      default:
        return null;
    }

    return (
      <>
        <OnboardingTopBar color={log_out_color} />
        <FloatingLayout
          success={success}
          onNextStep={() => {
            setSuccess(false);
            onChangeStep(step ? getStepPosition() + 1 : 0);
          }}
          onboardingStepProps={onboardingStepProps}
          left={leftElement}
          right={rightElement}
          leftStyle={leftElementStyle.style}
          leftWidthPercentage={leftElementStyle.widthPercent}
          background={backgroundElement}
        />
      </>
    );
  };

  return (
    <OnboardingContainer container direction="column" wrap="nowrap" alignItems="center">
      {getStepContent()}
    </OnboardingContainer>
  );
};
